import React from "react";
import { Coins } from "lucide-react";
import { REWARDS } from "@/constants/testIds";

/**
 * Points + $ equivalent balance card. 100 pts = $1; the redemption
 * minimum comes from /points/terms via the `minRedeem` prop.
 * Rendered as the sticky-top hero panel of the Rewards hub.
 */
export default function BalanceCard({ balance = 0, accountEmail = "", minRedeem = 50, split = null }) {
  const dollars = (balance / 100).toFixed(2);
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
  return (
    <div
      data-testid={REWARDS.balance}
      className="rounded-3xl p-6 md:p-8 bg-gradient-to-br from-[color:var(--brand-blue)] to-[color:var(--brand-blue-hover)] text-white shadow-card"
    >
      <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-[0.24em]">
        <Coins className="w-3.5 h-3.5" /> Your rewards balance · NZ stores
      </div>
      <div className="mt-3 flex items-end gap-3 flex-wrap">
        <div
          data-testid={REWARDS.balancePoints}
          className="font-display text-5xl md:text-6xl font-black leading-none"
        >
          {balance.toLocaleString()}
          <span className="text-lg font-bold opacity-80 ml-2">pts</span>
        </div>
        <div
          data-testid={REWARDS.balanceDollars}
          className="text-sm font-semibold opacity-90 pb-2"
        >
          ≈ ${dollars} off
        </div>
      </div>
      {/* Iter-418 — account-scoped points: each login has its own
          balance, so name the account right under the number. */}
      {accountEmail && (
        <p className="mt-2 text-xs font-semibold text-white/85 break-all" data-testid="rewards-account-email">
          👤 Points for: {accountEmail} <span className="font-normal text-white/65">(each login has its own points)</span>
        </p>
      )}
      {/* Iter-622 — split view: purchased/gifted (3-year expiry, unlock
          the no-review pay path) vs earned (12-month expiry). */}
      {split && (
        <div className="mt-3 rounded-xl bg-white/[0.12] p-3" data-testid="rw-wallet-split">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-extrabold">💳 Purchased / gifted</span>
            <span className="text-[15px] font-black" data-testid="rw-split-purchased">
              {Number(split.purchased || 0).toLocaleString()} pts
            </span>
          </div>
          <p className="mt-0.5 text-[11px] leading-[15px] text-sky-100">
            {split.purchased > 0 && split.purchasedExp
              ? `Valid until ${fmtDate(split.purchasedExp)} (3 years) · pay a whole bill with points at any store, no review needed`
              : "Buy or receive points as a gift — 3-year validity, spend at any store without a review"}
          </p>
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <span className="text-[13px] font-extrabold">⭐ Earned</span>
            <span className="text-[15px] font-black" data-testid="rw-split-earned">
              {Number(split.earned || 0).toLocaleString()} pts
            </span>
          </div>
          <p className="mt-0.5 text-[11px] leading-[15px] text-sky-100">
            {split.earned > 0 && split.earnedExp
              ? `Next expiry ${fmtDate(split.earnedExp)} (12 months from earning) · spend via the review path`
              : "Check in, review and share at partner stores to earn · 12-month validity"}
          </p>
        </div>
      )}
      <p className="mt-3 text-xs text-white/70 max-w-md">
        Earn a point every visit at partner stores. Redeem 100 pts for $1 off (from {minRedeem} pts).
      </p>
      {/* Iter 629 — the "Buy points" CTA moved to the wallet tabs above. */}
    </div>
  );
}
